import { startTransition, useCallback } from "react"

import isEmpty from "lodash-es/isEmpty"
import { type NavigateFunction, useNavigate } from "react-router-dom"

import { setCurrentAccessToken, setCurrentRefreshToken } from "$/utils/tokens"

import { INDEX_URL } from "@/constants"
import type { IJwtTokenPairDto } from "@/models/jwt"
import {
    EStorageServiceType,
    LocalStorageServiceKeys,
    SessionStorageServiceKeys,
    StorageService,
    type TStoredSubUser
} from "@/services"

const localStorageService: StorageService = new StorageService(EStorageServiceType.Local)
const sessionStorageService: StorageService = new StorageService(EStorageServiceType.Session)

type TUseSecondaryAuth = {
    reloadUser: ({ userId }: { userId: string }) => void
}

function useSecondaryAuth(): ReturnType<() => TUseSecondaryAuth> {
    const navigate: NavigateFunction = useNavigate()

    const reloadUser: ({ userId }: { userId: string }) => void = useCallback(
        ({ userId }: { userId: string }): void => {
            const { access: accessToken, refresh: refreshToken }: IJwtTokenPairDto = localStorageService
                .getItem<TStoredSubUser[] | null>(LocalStorageServiceKeys.SubUserList())
                ?.find((u: TStoredSubUser): boolean => u?.id === Number(userId))?.jwtPair ?? {
                access: String(),
                refresh: String()
            }

            if (!isEmpty(userId) && !isEmpty(accessToken) && !isEmpty(refreshToken)) {
                sessionStorageService.setItem<boolean>(SessionStorageServiceKeys.FakeParticipantsFeatureEnabled(), true)

                setCurrentAccessToken(accessToken)
                void setCurrentRefreshToken(refreshToken)

                startTransition((): void => window.location.replace(INDEX_URL)) // can't navigate properly with useNavigate()
            } else {
                startTransition((): void => (navigate(INDEX_URL, { replace: true }), void 0))
            }
        },
        [navigate]
    )

    return { reloadUser }
}

export { useSecondaryAuth, type TUseSecondaryAuth }
